import React from 'react';
import { Container, Typography, Box, Paper, Chip, Alert, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import VerifiedIcon from '@mui/icons-material/Verified';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const PrivacySection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  border: '1px solid #E5E7EB',
  background: '#FEFEFE',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: '#4338CA',
  },
}));

const Privacy = () => {
  const { t } = useTranslation();
  const whatWeDontStoreItems = t('privacy.whatWeDontStore.items', { returnObjects: true }) as string[];
  
  // Set page title for analytics
  usePageTitle('Privacy Policy');

  return (
    <PrivacySection>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h1" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              mb: 3,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('privacy.title')}
          </Typography>

          <Typography 
            variant="h2" 
            align="center" 
            paragraph
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              color: '#6B7280',
              lineHeight: 1.6,
            }}
          >
            {t('privacy.subtitle')}
          </Typography>
        </motion.div>

        <Alert 
          severity="success" 
          icon={<SecurityIcon aria-hidden="true" />}
          sx={{ 
            mb: 6,
            maxWidth: '600px',
            mx: 'auto',
            fontSize: '1.1rem',
            background: 'rgba(16, 185, 129, 0.04)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 3,
          }}
        >
          <strong>{t('privacy.guarantee').split(':')[0]}:</strong>{t('privacy.guarantee').split(':')[1]}
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FeatureCard>
              <CloudOffIcon sx={{ fontSize: '3rem', color: '#10B981', mb: 2 }} aria-hidden="true" />
              <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {t('privacy.noCloud.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#6B7280', lineHeight: 1.6 }}>
                {t('privacy.noCloud.description')}
              </Typography>
            </FeatureCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <FeatureCard>
              <DeleteIcon sx={{ fontSize: '3rem', color: '#10B981', mb: 2 }} aria-hidden="true" />
              <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
                {t('privacy.automaticDeletion.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#6B7280', lineHeight: 1.6 }}>
                {t('privacy.automaticDeletion.description')}
              </Typography>
            </FeatureCard>
          </motion.div>
        </Box>

        <Paper 
          sx={{ 
            p: { xs: 3, sm: 6 }, 
            mb: 8, 
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
            border: '1px solid rgba(94, 106, 210, 0.1)',
          }}
        >
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '2rem', textAlign: 'center', mb: 4 }}>
            {t('privacy.process.title')}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Chip label={t('upload.requirements.step', { count: 1 })} color="primary" sx={{ mb: 2 }} />
              <Typography variant="h3" gutterBottom sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {t('privacy.process.step1.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#6B7280' }}>
                {t('privacy.process.step1.description')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Chip label={t('upload.requirements.step', { count: 2 })} color="primary" sx={{ mb: 2 }} />
              <Typography variant="h3" gutterBottom sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {t('privacy.process.step2.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#6B7280' }}>
                {t('privacy.process.step2.description')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Chip label={t('upload.requirements.step', { count: 3 })} color="primary" sx={{ mb: 2 }} />
              <Typography variant="h3" gutterBottom sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {t('privacy.process.step3.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#6B7280' }}>
                {t('privacy.process.step3.description')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mb: 8 }}>
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '2rem', textAlign: 'center', mb: 4 }}>
            {t('privacy.whatWeDontStore.title')}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, maxWidth: '800px', mx: 'auto' }}>
            {whatWeDontStoreItems.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }}>
                <VerifiedIcon sx={{ color: '#10B981', mr: 2 }} aria-hidden="true" />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Paper sx={{ p: { xs: 3, sm: 6 }, borderRadius: 4, textAlign: 'center', bgcolor: '#F9FAFB' }}>
          <SecurityIcon sx={{ fontSize: '3rem', color: '#5E6AD2', mb: 3 }} aria-hidden="true" />
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
            {t('privacy.reportPolicy.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', maxWidth: '600px', mx: 'auto', lineHeight: 1.6, mb: 3 }}>
            {t('privacy.reportPolicy.description')}
          </Typography>
          <Chip 
            label={t('privacy.reportPolicy.chip')}
            color="primary" 
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Paper>

        <Box sx={{ mt: 8 }}>
        <Paper 
          sx={{ 
            p: { xs: 3, sm: 6 }, 
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
            border: '1px solid rgba(94, 106, 210, 0.1)',
          }}
        >
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '2rem', mb: 3, fontWeight: 700 }}>
            {t('privacy.cta.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', maxWidth: '600px', mx: 'auto', mb: 4, lineHeight: 1.6 }}>
            {t('privacy.cta.description')}
          </Typography>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              variant="contained" 
              size="large"
              component={RouterLink}
              to="/upload"
              sx={{ 
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4C5BC7 0%, #7C3AED 100%)',
                }
              }}
            >
              {t('privacy.cta.button')}
            </Button>
          </motion.div>
        </Paper>
        </Box>
      </Container>
    </PrivacySection>
  );
};

export default Privacy; 